import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Spinner } from '../spinner'
import { whatsappTemplateQueries } from 'src/services/whatsapp/queries'
import { Conversation, MessageCreate, WhatsappTemplate } from 'src/services/whatsapp'
import { useTemplateSendValues } from 'src/services/whatsapp/use-template-bindings'
import { useAuth } from 'src/stores/auth'
import { NoApprovedTemplateNotice } from './no-approved-template-notice'
import { TemplateVariablePrompts } from './template-variable-prompts'

/** Everything a send needs except the conversation the composer already knows. */
export type SendPayload = Omit<MessageCreate, 'conversation'>

interface TemplateComposerProps {
  conversation: Conversation
  onSend: (payload: SendPayload) => void
  isLoading?: boolean
}

// Allows a dot so a canonical token like `reservation.checkin_date` substitutes
// as one variable, matching `variablesIn`'s pattern in services/whatsapp/bindings.ts.
const PLACEHOLDER_RE = /{{\s*([\w.]+)\s*}}/g

export function renderBody(body: string, values: Record<string, string>): string {
  return body.replace(PLACEHOLDER_RE, (placeholder, name: string) => values[name] || placeholder)
}

/** One template, with every approved language it exists in. A template name is
 * stable across languages, so the picker offers names and the language is a
 * second, independent choice. */
export type TemplateGroup = {
  name: string
  isDefault: boolean
  byLanguage: Record<string, WhatsappTemplate>
}

export function groupTemplatesByName(templates: WhatsappTemplate[]): TemplateGroup[] {
  const byName: Record<string, TemplateGroup> = {}
  templates.forEach((template) => {
    const group = (byName[template.name] ??= {
      name: template.name,
      isDefault: false,
      byLanguage: {},
    })
    group.byLanguage[template.language] = template
    group.isDefault = group.isDefault || template.isDefault
  })
  // The platform default opens conversations, so it leads the list.
  return Object.values(byName).sort(
    (a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name),
  )
}

/**
 * Template-mode composer, shown when the 24-hour window is closed (or the
 * customer has never written). WhatsApp allows nothing but an approved template
 * there, so this offers a template, the language to send it in, and inputs for
 * the variables its body references — never a free-text body.
 */
export const TemplateComposer = ({
  conversation,
  onSend,
  isLoading = false,
}: TemplateComposerProps) => {
  const { t } = useTranslation()
  const activeOrgId = useAuth.use.activeOrgId()
  const user = useAuth.use.user()
  const orgName =
    user?.memberships?.find((m) => m.organizationId === activeOrgId)?.organizationName ?? ''

  const { data: templatesPage, isLoading: isLoadingTemplates } = useQuery(
    whatsappTemplateQueries.approved(activeOrgId),
  )
  const groups = useMemo(
    () => groupTemplatesByName(templatesPage?.results ?? []),
    [templatesPage?.results],
  )

  const [templateName, setTemplateName] = useState<string | null>(null)
  const [language, setLanguage] = useState<string | null>(null)

  const group = groups.find((g) => g.name === templateName) ?? groups[0] ?? null
  const languages = group ? Object.keys(group.byLanguage).sort() : []
  // The contact's curated language is the default; an explicit pick wins, but
  // only while it is still one of this template's approved languages.
  const contactLanguage = conversation.contactRef?.language ?? null
  const selectedLanguage =
    (language && languages.includes(language) ? language : null) ??
    (contactLanguage && languages.includes(contactLanguage) ? contactLanguage : null) ??
    languages[0] ??
    null
  const template = group && selectedLanguage ? group.byLanguage[selectedLanguage] : null

  const { bindings, promptedVars, overrides, setOverride, values, hasEveryValue, manualValues } =
    useTemplateSendValues(template, conversation, orgName)

  const handleSend = () => {
    if (!template || !hasEveryValue || isLoading) return
    onSend({
      templateName: template.name,
      templateLanguage: template.language,
      templateVariables: manualValues,
    })
  }

  if (isLoadingTemplates) {
    return (
      <div className="flex items-center justify-center border-t border-border bg-surface p-4">
        <Spinner size="sm" />
      </div>
    )
  }

  // No approved template means nothing can be sent outside the window. Explain
  // an in-review template as a wait, otherwise point at the admin screen —
  // never a raw rejection or a dead-end composer.
  if (!template) {
    return (
      <div className="flex flex-col items-start gap-1 border-t border-border bg-surface p-4">
        <NoApprovedTemplateNotice organization={activeOrgId}>
          <div
            className="flex flex-col items-start gap-1"
            data-testid="composer-no-approved-template"
          >
            <p className="text-sm text-error-600">{t('chat.noApprovedTemplate')}</p>
            <Link
              to="/organization-settings"
              className="text-sm font-medium text-primary underline hover:no-underline"
            >
              {t('chat.reviewTemplates')}
            </Link>
          </div>
        </NoApprovedTemplateNotice>
      </div>
    )
  }

  return (
    <div
      className="flex flex-col gap-3 border-t border-border bg-surface p-4"
      data-testid="template-composer"
    >
      <div className="flex flex-wrap gap-3">
        <div className="flex min-w-48 flex-1 flex-col gap-1">
          <label htmlFor="composer-template" className="text-sm font-medium text-primary">
            {t('chat.templateLabel')}
          </label>
          <select
            id="composer-template"
            value={group.name}
            onChange={(e) => setTemplateName(e.target.value)}
            disabled={isLoading}
            data-testid="composer-template-select"
            className="w-full rounded-md border bg-surface px-3 py-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
          >
            {groups.map((option) => (
              <option key={option.name} value={option.name}>
                {option.isDefault
                  ? `${option.name} (${t('chat.defaultTemplateLabel')})`
                  : option.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex w-40 flex-col gap-1">
          <label htmlFor="composer-template-language" className="text-sm font-medium text-primary">
            {t('chat.templateLanguageLabel')}
          </label>
          <select
            id="composer-template-language"
            value={template.language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={isLoading}
            data-testid="composer-template-language"
            className="w-full rounded-md border bg-surface px-3 py-2 text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
          >
            {languages.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </div>

      <TemplateVariablePrompts
        promptedVars={promptedVars}
        bindings={bindings}
        overrides={overrides}
        setOverride={setOverride}
        disabled={isLoading}
        testIdPrefix="composer"
      />

      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
          {t('chat.templatePreviewLabel')}
        </p>
        <p
          className="rounded-md border border-border bg-surface-hover px-3 py-2 text-sm text-text-secondary"
          data-testid="composer-template-preview"
        >
          {renderBody(template.body, values)}
        </p>
      </div>

      <Button
        type="button"
        variant="primary"
        onClick={handleSend}
        isLoading={isLoading}
        disabled={isLoading || !hasEveryValue}
        className="self-end"
      >
        <PaperAirplaneIcon className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('chat.sendTemplate')}
      </Button>
    </div>
  )
}
